'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { c } from '../../../utils/tailwind-merge';
import { Button, IconButton } from '../../atoms/Button';
import { RichText } from '../RichText';
import { FormModalProps } from './FormModal.types';

const modalVariants = cva('', {
  variants: {
    size: {
      xsmall: [
        'w-full h-[50%] max-h-[406px]',
        'md:w-6/12 h-[50%] md:max-h-[540px]',
        'lg:w-4/12 h-[50%] lg:max-h-[540px]',
      ].join(' '),
      small: [
        'w-full h-[70%] max-h-[568px]',
        'md:w-8/12 h-[70%] md:max-h-[756px]',
        'lg:w-6/12 h-[70%] lg:max-h-[756px]',
      ].join(' '),
      medium: [
        'w-full h-[84%] max-h-[682px]',
        'md:w-10/12 h-[84%] md:max-h-[908px]',
        'lg:w-8/12 h-[84%] lg:max-h-[908px]',
      ].join(' '),
      large: [
        'w-full h-full',
        'md:w-[calc(100%-64px)] md:max-h-[96%]',
        'lg:w-10/12 lg:max-h-[96%]',
      ].join(' '),
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

const contentBaseClasses =
  'z-modal fixed overflow-y-auto ' +
  'bg-white shadow-card outline-none border border-grey-200 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover ' +
  'px-5 py-4 ' +
  'left-0 right-0 bottom-0 rounded-t-lg ' +
  'md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:rounded-lg';

const contentTransitionClasses =
  'transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] ' +
  'data-[state=closed]:translate-y-full data-[state=open]:translate-y-0 ' +
  'data-[state=closed]:opacity-0 data-[state=open]:opacity-100 ' +
  'md:data-[state=closed]:scale-95 md:data-[state=open]:scale-100 ' +
  'md:data-[state=closed]:-translate-y-1/2 md:data-[state=open]:-translate-y-1/2';

export const FormModal = (props: FormModalProps) => {
  const {
    title,
    close,
    trigger,
    content,
    footer,
    size,
    submit,
    cancel,
    onSubmit,
    onCancel,
    className = '',
    disabled = false,
  } = props;

  const handleSubmit = () => {
    onSubmit();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Dialog.Root>
      {trigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className="z-overlay fixed inset-0 bg-black/50 transition duration-300 data-[state=closed]:animate-overlayHide data-[state=open]:animate-overlayShow" />
        <Dialog.Content
          className={c(
            contentBaseClasses,
            contentTransitionClasses,
            modalVariants({ size }),
            className
          )}
        >
          <div className="flex h-full flex-col gap-4 md:gap-4">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-xl font-semibold leading-7 text-grey-900 md:text-2xl md:leading-8">
                {title}
              </Dialog.Title>
              <Dialog.Close asChild>
                <IconButton
                  iconName="X"
                  ariaLabel={close}
                  className="h-12 w-12 rounded-full bg-transparent p-1 text-grey-900 hover:bg-grey-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover"
                  iconClassName="h-6 w-6 fill-grey-900"
                  onClick={handleCancel}
                />
              </Dialog.Close>
            </div>
            <div className="flex flex-1 flex-col gap-4 md:gap-6">
              {content ? (
                <div className="flex flex-1 flex-col">{content}</div>
              ) : null}
              <div className="mt-auto">
                <div className="mb-4 flex flex-col gap-3 md:flex-col md:gap-3 lg:flex-row lg:items-center lg:justify-end lg:gap-4">
                  <Button.v2
                    label={submit}
                    disabled={disabled}
                    className="order-1 w-full lg:order-2 lg:w-auto"
                    variant="primary"
                    size="large"
                    shape="square"
                    onClick={handleSubmit}
                  />
                  {cancel ? (
                    <Dialog.Close asChild>
                      <Button.v2
                        label={cancel}
                        className="order-2 w-full lg:order-1 lg:w-auto"
                        variant="tertiary-black-monochrome"
                        size="large"
                        shape="square"
                        onClick={handleCancel}
                      />
                    </Dialog.Close>
                  ) : null}
                </div>
                {footer ? (
                  <div className="space-y-1 border-t border-grey-300 pt-4">
                    <p className="text-sm font-medium leading-5 tracking-tight text-grey-900">
                      {footer.title}
                    </p>
                    <RichText
                      html={footer.description}
                      className="text-sm font-normal leading-5 tracking-tight text-grey-600 [&_a]:text-sm"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
